import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { User, Phone, Building2, Lock, Mail, Calendar, CreditCard, Loader2, Monitor, Trash2, ShieldCheck, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrustedDevicesSection = ({ token }) => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchDevices = async () => {
    try {
      const res = await axios.get(`${API}/auth/trusted-devices`, { headers: { Authorization: `Bearer ${token}` } });
      setDevices(res.data.devices || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDevices(); }, []);

  const removeDevice = async (deviceId) => {
    setDeleting(deviceId);
    try {
      await axios.delete(`${API}/auth/trusted-devices/${deviceId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDevices(prev => prev.filter(d => d.id !== deviceId));
      toast.success(t('Dispositivo eliminado'));
    } catch { toast.error(t('Error al eliminar')); }
    finally { setDeleting(null); }
  };

  if (loading) return <div className="text-zinc-500 text-sm p-4">{t('Cargando dispositivos...')}</div>;

  return (
    <div className="space-y-3">
      {devices.length === 0 ? (
        <p className="text-zinc-500 text-sm">{t('No hay dispositivos de confianza registrados.')}</p>
      ) : (
        devices.map(device => (
          <div key={device.id} className="flex items-center justify-between p-3 bg-surface-highlight rounded-md">
            <div className="flex items-center gap-3">
              <Monitor className="w-5 h-5 text-primary flex-shrink-0" />
              <div>
                <p className="text-white text-sm font-medium">{device.device_name}</p>
                <p className="text-zinc-500 text-xs">
                  IP: {device.ip} · {t('Último uso')}: {new Date(device.last_used).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
                <p className="text-zinc-600 text-xs">
                  {t('Caduca')}: {new Date(device.expires_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeDevice(device.id)}
              disabled={deleting === device.id}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              {deleting === device.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          </div>
        ))
      )}
    </div>
  );
};

const ProfilePage = () => {
  const { user, token, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [companyName, setCompanyName] = useState(user?.company_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [isCompany, setIsCompany] = useState(user?.is_company || false);
  const [fiscalData, setFiscalData] = useState({
    fiscal_name: '',
    cif: '',
    vat_number: '',
    fiscal_address: '',
    fiscal_city: '',
    fiscal_postal_code: '',
    fiscal_province: '',
    fiscal_country: 'España',
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingFiscal, setSavingFiscal] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (user?.fiscal_data) {
      setFiscalData(prev => ({ ...prev, ...user.fiscal_data }));
    }
    if (user?.is_company !== undefined) {
      setIsCompany(user.is_company);
    }
  }, [user]);

  const handleFiscalChange = (e) => {
    setFiscalData({ ...fiscalData, [e.target.name]: e.target.value });
  };

  const saveProfile = async () => {
    if (!companyName.trim()) return toast.error(t('El nombre de empresa es obligatorio'));
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('company_name', companyName);
      fd.append('phone', phone);
      await axios.put(`${API}/auth/profile`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('Perfil actualizado'));
      await refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || t('Error al guardar')); }
    finally { setSaving(false); }
  };

  const saveFiscalInfo = async () => {
    setSavingFiscal(true);
    try {
      const fd = new FormData();
      fd.append('is_company', isCompany.toString());
      fd.append('fiscal_name', fiscalData.fiscal_name || '');
      fd.append('cif', fiscalData.cif || '');
      fd.append('vat_number', fiscalData.vat_number || '');
      fd.append('fiscal_address', fiscalData.fiscal_address || '');
      fd.append('fiscal_city', fiscalData.fiscal_city || '');
      fd.append('fiscal_postal_code', fiscalData.fiscal_postal_code || '');
      fd.append('fiscal_province', fiscalData.fiscal_province || '');
      fd.append('fiscal_country', fiscalData.fiscal_country || 'España');
      await axios.put(`${API}/auth/profile`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('Datos fiscales actualizados'));
      await refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || t('Error al guardar datos fiscales')); }
    finally { setSavingFiscal(false); }
  };

  const changePassword = async () => {
    if (!currentPassword) return toast.error(t('Introduce tu contraseña actual'));
    if (newPassword.length < 8) return toast.error(t('Mínimo 8 caracteres'));
    if (newPassword !== confirmPassword) return toast.error(t('Las contraseñas no coinciden'));
    setSavingPassword(true);
    try {
      const fd = new FormData();
      fd.append('current_password', currentPassword);
      fd.append('new_password', newPassword);
      await axios.put(`${API}/auth/profile`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('Contraseña actualizada'));
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || t('Error al cambiar contraseña')); }
    finally { setSavingPassword(false); }
  };

  const inputClass = "bg-surface-highlight border-white/10 text-white";

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="profile-page">
        <h1 className="font-heading text-3xl font-bold text-white mb-6">{t('MI PERFIL')}</h1>

        {/* Account Info */}
        <div className="bg-surface border border-white/10 rounded-md p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-white font-bold">{user?.company_name}</p>
              <p className="text-zinc-500 text-sm">{user?.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-zinc-400"><Mail className="w-4 h-4" /><span>{user?.email}</span></div>
            <div className="flex items-center gap-2 text-zinc-400"><CreditCard className="w-4 h-4" /><span>{user?.credits || 0} {t('créditos')}</span></div>
            <div className="flex items-center gap-2 text-zinc-400"><Calendar className="w-4 h-4" /><span>{t('Miembro desde')} {user?.created_at ? new Date(user.created_at).toLocaleDateString('es-ES') : '-'}</span></div>
            <div className="flex items-center gap-2 text-zinc-400">
              <div className={`w-2 h-2 rounded-full ${user?.email_verified ? 'bg-green-500' : 'bg-red-500'}`} />
              <span>{user?.email_verified ? t('Email verificado') : t('Email sin verificar')}</span>
            </div>
            {user?.holded_contact_id && (
              <div className="flex items-center gap-2 text-green-400 col-span-2">
                <FileText className="w-4 h-4" />
                <span>{t('Facturación automática activa (Holded)')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Edit Profile */}
        <div className="bg-surface border border-white/10 rounded-md p-6 mb-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />{t('DATOS PERSONALES')}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">{t('Nombre de empresa / Taller')}</label>
              <Input value={companyName} onChange={e => setCompanyName(e.target.value)}
                className={inputClass} />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">{t('Teléfono (con prefijo, ej: +34600123456)')}</label>
              <div className="flex gap-2">
                <Phone className="w-5 h-5 text-zinc-500 self-center" />
                <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+34600123456"
                  className={inputClass} />
              </div>
              <p className="text-zinc-600 text-xs mt-1">{t('Se usa para notificaciones SMS y WhatsApp')}</p>
            </div>
            <Button onClick={saveProfile} disabled={saving} className="bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('Guardar Cambios')}
            </Button>
          </div>
        </div>

        {/* Fiscal Data */}
        <div className="bg-surface border border-white/10 rounded-md p-6 mb-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />{t('DATOS FISCALES')}
          </h2>
          <p className="text-zinc-500 text-sm mb-4">
            {t('Completa tus datos fiscales para la facturación automática.')}
          </p>

          {/* Company toggle */}
          <div className="flex gap-2 mb-5">
            <button
              type="button"
              onClick={() => setIsCompany(false)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md border text-sm font-medium transition-all ${
                !isCompany ? 'bg-primary/10 border-primary text-primary' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'
              }`}
            >
              <User className="w-4 h-4" />{t('Particular')}
            </button>
            <button
              type="button"
              onClick={() => setIsCompany(true)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md border text-sm font-medium transition-all ${
                isCompany ? 'bg-primary/10 border-primary text-primary' : 'bg-surface-highlight border-white/10 text-zinc-400 hover:text-white'
              }`}
            >
              <Building2 className="w-4 h-4" />{t('Empresa')}
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-zinc-400 text-xs mb-1 block">{isCompany ? t('Razón Social') : t('Nombre Completo')}</label>
                <Input name="fiscal_name" value={fiscalData.fiscal_name} onChange={handleFiscalChange}
                  placeholder={isCompany ? "Mi Empresa S.L." : "Juan García López"}
                  className={inputClass} />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{isCompany ? 'CIF' : 'DNI/NIE'}</label>
                <Input name="cif" value={fiscalData.cif} onChange={handleFiscalChange}
                  placeholder={isCompany ? "B12345678" : "12345678A"}
                  className={inputClass} />
              </div>
              {isCompany && (
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">{t('Nº IVA Intracomunitario')}</label>
                  <Input name="vat_number" value={fiscalData.vat_number} onChange={handleFiscalChange}
                    placeholder="ESB12345678" className={inputClass} />
                </div>
              )}
              <div className={isCompany ? 'col-span-2' : ''}>
                <label className="text-zinc-400 text-xs mb-1 block">{t('Dirección Fiscal')}</label>
                <Input name="fiscal_address" value={fiscalData.fiscal_address} onChange={handleFiscalChange}
                  placeholder="Calle Principal, 1" className={inputClass} />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('Ciudad')}</label>
                <Input name="fiscal_city" value={fiscalData.fiscal_city} onChange={handleFiscalChange}
                  placeholder="Cáceres" className={inputClass} />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('Código Postal')}</label>
                <Input name="fiscal_postal_code" value={fiscalData.fiscal_postal_code} onChange={handleFiscalChange}
                  placeholder="10001" className={inputClass} />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('Provincia')}</label>
                <Input name="fiscal_province" value={fiscalData.fiscal_province} onChange={handleFiscalChange}
                  placeholder="Cáceres" className={inputClass} />
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{t('País')}</label>
                <Input name="fiscal_country" value={fiscalData.fiscal_country} onChange={handleFiscalChange}
                  placeholder="España" className={inputClass} />
              </div>
            </div>
            <Button onClick={saveFiscalInfo} disabled={savingFiscal} className="bg-primary hover:bg-primary/90">
              {savingFiscal ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('Guardar Datos Fiscales')}
            </Button>
          </div>
        </div>

        {/* Trusted Devices */}
        <div className="bg-surface border border-white/10 rounded-md p-6 mb-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />{t('DISPOSITIVOS DE CONFIANZA')}
          </h2>
          <p className="text-zinc-500 text-sm mb-4">
            {t('Estos dispositivos no requieren verificación en dos pasos al iniciar sesión. Si eliminas uno, se te pedirá un código la próxima vez que inicies sesión desde ese dispositivo.')}
          </p>
          <TrustedDevicesSection token={token} />
        </div>

        {/* Change Password */}
        <div className="bg-surface border border-white/10 rounded-md p-6">
          <h2 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />{t('CAMBIAR CONTRASEÑA')}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">{t('Contraseña actual')}</label>
              <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)}
                className={inputClass} />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">{t('Nueva contraseña')}</label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                className={inputClass} />
            </div>
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">{t('Confirmar nueva contraseña')}</label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                className={inputClass} />
            </div>
            <Button onClick={changePassword} disabled={savingPassword} variant="outline" className="border-white/10 text-white">
              {savingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('Cambiar Contraseña')}
            </Button>
          </div>
        </div>

        {/* Login History */}
        <LoginHistory />
      </div>
    </Layout>
  );
};

const LoginHistory = () => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auth/login-history`).then(r => {
      setHistory(r.data.history || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const flagUrl = (code) => code ? `https://flagcdn.com/24x18/${code}.png` : '';

  if (loading) return null;
  if (history.length === 0) return null;

  return (
    <div className="bg-surface border border-white/10 rounded-md p-6" data-testid="login-history">
      <h3 className="font-heading text-white font-bold text-sm mb-4">{t('HISTORIAL DE SESIONES')}</h3>
      <div className="space-y-2">
        {history.map((h, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
            <div className="w-6 flex-shrink-0">
              {h.country_code ? (
                <img src={flagUrl(h.country_code)} alt={h.country} className="w-6 h-4 rounded-sm object-cover" />
              ) : (
                <div className="w-6 h-4 bg-zinc-700 rounded-sm" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-mono">{h.ip}</p>
              <p className="text-zinc-500 text-xs">{h.city}{h.city && h.country ? ', ' : ''}{h.country}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-zinc-400 text-xs">{new Date(h.timestamp).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
              <p className="text-zinc-600 text-[10px]">{new Date(h.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfilePage;
